import React, { useState } from 'react';
import comms from '../../comms';

function InitialStopSelectionModal({ 
  isOpen, 
  onClose, 
  stops, 
  vehiclePosition, 
  googleApiKey, 
  routeId, 
  vehicleId, 
  runId,
  onRouteBegin, // Callback for route selection mode
  routeData, // Route data passed from parent instead of localStorage
  userSettings // User settings passed from parent instead of localStorage
}) {
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [etaTime, setEtaTime] = useState('');
  const [calculatingETA, setCalculatingETA] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate ETA using Google Distance Matrix API
  const calculateETA = async (stop) => {
    if (!stop) return;

    console.log('calculateETA called with:', { stop: stop?.Name, vehiclePosition, googleApiKey: !!googleApiKey });
    
    setCalculatingETA(true);
    try {
      // Check if we have vehicle position
      if (!vehiclePosition) {
        console.log('Vehicle position not available, using default ETA of 5 minutes');
        // Set default ETA of 5 minutes if position is not available
        const now = new Date();
        const defaultArrival = new Date(now.getTime() + (5 * 60 * 1000));
        setEtaTime(defaultArrival.toTimeString().substring(0, 5));
        setCalculatingETA(false);
        return;
      }

      // Check if Google Maps API is available
      if (!googleApiKey || !window.google || !window.google.maps) {
        console.log('Google Maps API not available, calculating basic ETA');
        
        // Calculate basic distance and estimate time (rough calculation)
        const lat1 = vehiclePosition.lat;
        const lng1 = vehiclePosition.lng;
        const lat2 = parseFloat(stop.Lat);
        const lng2 = parseFloat(stop.Lon);
        
        // Simple distance calculation (Haversine formula approximation)
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                 Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                 Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c; // Distance in km
        
        // Estimate time at average speed of 30 km/h in city
        const estimatedMinutes = Math.ceil((distance / 30) * 60);
        const etaMinutes = Math.max(2, estimatedMinutes); // Minimum 2 minutes
        
        const now = new Date();
        const estimatedArrival = new Date(now.getTime() + (etaMinutes * 60 * 1000));
        setEtaTime(estimatedArrival.toTimeString().substring(0, 5));
        setCalculatingETA(false);
        return;
      }

      const service = new window.google.maps.DistanceMatrixService();
      
      const result = await new Promise((resolve, reject) => {
        service.getDistanceMatrix({
          origins: [{ lat: vehiclePosition.lat, lng: vehiclePosition.lng }],
          destinations: [{ lat: parseFloat(stop.Lat), lng: parseFloat(stop.Lon) }],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
          avoidHighways: false,
          avoidTolls: false
        }, (response, status) => {
          if (status === window.google.maps.DistanceMatrixStatus.OK) {
            resolve(response);
          } else {
            reject(new Error(`Distance Matrix API error: ${status}`));
          }
        });
      });

      if (result.rows[0]?.elements[0]?.status === 'OK') {
        const duration = result.rows[0].elements[0].duration.value; // in seconds
        const etaMinutes = Math.ceil(duration / 60);
        
        // Calculate arrival time
        const now = new Date();
        const arrivalTime = new Date(now.getTime() + (etaMinutes * 60 * 1000));
        
        // Format as HH:MM for the input
        const formattedTime = arrivalTime.toTimeString().substring(0, 5);
        setEtaTime(formattedTime);
      } else {
        console.error('No route found to destination');
        // Set default ETA of 5 minutes if no route found
        const now = new Date();
        const defaultArrival = new Date(now.getTime() + (5 * 60 * 1000));
        setEtaTime(defaultArrival.toTimeString().substring(0, 5));
      }
    } catch (error) {
      console.error('Error calculating ETA:', error);
      // Set default ETA of 5 minutes on error
      const now = new Date();
      const defaultArrival = new Date(now.getTime() + (5 * 60 * 1000));
      setEtaTime(defaultArrival.toTimeString().substring(0, 5));
    } finally {
      setCalculatingETA(false);
    }
  };

  // Handle stop selection
  const handleStopSelect = (stop) => {
    setSelectedStopId(stop.Id);
    calculateETA(stop);
  };

  // Handle begin route button click
  const handleBeginRoute = async () => {
    if (!selectedStopId || !etaTime) return;

    console.log('InitialStopSelection: handleBeginRoute called with:', {
      selectedStopId,
      etaTime,
      userSettings,
      routeData,
      onRouteBegin: !!onRouteBegin
    });

    setIsSubmitting(true);
    try {
      const selectedStop = stops.find(s => s.Id === selectedStopId);
      
      // Convert time to timestamp
      const today = new Date();
      const [hours, minutes] = etaTime.split(':').map(Number);
      const etaTimestamp = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      
      console.log('Beginning route with initial stop:', selectedStop.Name, 'ETA:', etaTimestamp);

      // Check if we have a route selection callback (for route selection mode)
      if (onRouteBegin && routeData) {
        console.log('Route selection mode - handling locally');
        
        // Ensure we have userSettings - try multiple sources if needed
        if (!userSettings) {
          console.warn('InitialStopSelection: userSettings not available, checking localStorage');
          // Try to get from localStorage as fallback
          try {
            const storedSettings = localStorage.getItem('userSettings');
            if (storedSettings) {
              const parsedSettings = JSON.parse(storedSettings);
              console.log('InitialStopSelection: Found userSettings in localStorage:', parsedSettings);
            }
          } catch (error) {
            console.error('InitialStopSelection: Error reading userSettings from localStorage:', error);
          }
        }
        
        // Create start point log data locally
        const startPointLogData = {
          Id: `temp_${Date.now()}`, // Temporary ID for the start point
          StopId: selectedStopId,
          PAXBoarded: -1, // -1 indicates start point
          CreatedOn: new Date().toISOString(),
          UpdatedOn: etaTimestamp.toISOString(), // Use the selected ETA time
          RouteId: routeData.routeId,
          VehicleId: vehicleId,
          RunId: routeData.runId,
          Status: 'pending' // Mark as pending initial stop
        };
        
        // Update the route data with the new log entry
        const updatedRoute = {
          ...routeData,
          Logs: [startPointLogData], // Add logs directly to the route object for legacy compatibility
          logs: [startPointLogData] // Add to new format logs array
        };
        
        let driverId = userSettings?.Id || userSettings?.id || userSettings?.driverId || null;
        
        // If we still don't have driverId, try localStorage as fallback
        if (!driverId) {
          try {
            const storedSettings = localStorage.getItem('userSettings');
            if (storedSettings) {
              const parsedSettings = JSON.parse(storedSettings);
              driverId = parsedSettings?.Id || parsedSettings?.id || parsedSettings?.driverId || null;
              console.log('InitialStopSelection: Retrieved driverId from localStorage:', driverId);
            }
          } catch (error) {
            console.error('InitialStopSelection: Error parsing userSettings from localStorage:', error);
          }
        }
        
        console.log('InitialStopSelection: Creating LOOPED_ROUTE_BEGIN message with:', {
          driverId: driverId,
          userSettings: userSettings,
          routeId: routeId,
          vehicleId: vehicleId,
          selectedStopId: selectedStopId
        });
        
        // Warn if we don't have a driverId
        if (!driverId) {
          console.warn('InitialStopSelection: No driverId available - message will be sent without it');
        }
        
        // Create message data
        const messageData = {
          routeId: routeId,
          vehicleId: vehicleId,
          runId: runId,
          scheduleId: routeData.scheduleId,
          driverId: driverId,
          stopId: selectedStopId,
          itineraryId: selectedStop.ItineraryId || null,
          etaTimestamp: etaTimestamp.toISOString(),
          currentLocation: vehiclePosition
        };
        
        // Send message in the format expected by the sendSocket function: command|data
        const message = `LOOPED_ROUTE_BEGIN|${JSON.stringify(messageData)}`;
        
        // Send via socket (sendSocket will add unitId and messageId automatically)
        comms.sendSocket(message);
        
        //update the itineraryitem to mark beginStep true for the selected stop
        updatedRoute.itinerary = updatedRoute.itinerary.map(item => ({
          ...item,
          beginStep: item.StopId === selectedStopId,
          currentWaypoint: item.StopId === selectedStopId,
          eta: (item.StopId === selectedStopId ? etaTimestamp.toISOString() : item.eta)
        }));



        // Call the callback to handle route begin locally with updated route data
        onRouteBegin(startPointLogData, updatedRoute);
        
        // Close modal
        onClose();
      }
    } catch (error) {
      console.error('Error beginning route:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Don't render until we have userSettings or can find it in localStorage
  if (!userSettings) {
    try {
      const storedSettings = localStorage.getItem('userSettings');
      if (!storedSettings) {
        console.warn('InitialStopSelection: No userSettings available and none in localStorage');
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200] p-4">
            <div className="bg-white rounded-lg shadow-xl p-6">
              <p className="text-red-600">Error: User settings not available. Please try logging in again.</p>
            </div>
          </div>
        );
      }
    } catch (error) {
      console.error('InitialStopSelection: Error checking localStorage:', error);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
        <div className="bg-blue-600 text-white p-4">
          <h2 className="text-xl font-bold">Select Starting Stop</h2>
          <p className="text-sm text-blue-100">Choose which stop you'll visit first</p>
        </div>
        
        <div className="p-4 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {stops.slice(0, Math.round(stops.length / 2)).map((stop, index) => (
              <button
                key={stop.Id}
                onClick={() => handleStopSelect(stop)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedStopId === stop.Id
                    ? 'bg-blue-50 border-blue-300 text-blue-900'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="font-medium">{stop.Name}</div>
              </button>
            ))}
          </div>
        </div>
        
        {selectedStopId && (
          <div className="border-t p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimated Arrival Time
            </label>
            {!vehiclePosition && (
              <div className="mb-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ GPS position not available. Please set your expected arrival time manually.
              </div>
            )}
            {calculatingETA ? (
              <div className="flex items-center text-sm text-gray-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Calculating ETA...
              </div>
            ) : (
              <input
                type="time"
                value={etaTime}
                onChange={(e) => setEtaTime(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="HH:MM"
              />
            )}
            {etaTime && !calculatingETA && (
              <div className="mt-1 text-xs text-gray-500">
                {vehiclePosition ? 
                  (googleApiKey && window.google ? 'Calculated using GPS and traffic data' : 'Estimated based on distance') :
                  'Manual entry - adjust as needed'
                }
              </div>
            )}
          </div>
        )}
        
        <div className="border-t p-4 flex gap-3">

          <button
            onClick={handleBeginRoute}
            disabled={!selectedStopId || !etaTime || isSubmitting}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Beginning...
              </div>
            ) : (
              'Begin Route'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InitialStopSelectionModal;
